import { makeAutoObservable, runInAction } from 'mobx'
import organizationApi from '../tenancy/organizationApi'
import organizationSessionStore from './organizationSessionStore'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import {
	invitationRoleOptions,
	normalizeInvitationEmail,
	normalizeTeamName,
	safeOrganizationActionError,
} from '../tenancy/organizationUiPolicy'

export class OrganizationManagementStore {
	pendingInvitations = []
	incomingInvitations = []
	identityGeneration = 0
	incomingGeneration = 0
	createdInvitationToken = null
	creatingTeam = false
	creatingInvitation = false
	acceptingInvitation = false
	error = null

	constructor({ api = organizationApi, session = organizationSessionStore } = {}) {
		this.api = api
		this.session = session
		makeAutoObservable(this, { api: false, session: false }, { autoBind: true })
	}

	get invitationRoles() {
		return invitationRoleOptions(this.session.selectedOrganization, this.session.membershipRole)
	}

	get canInvite() {
		return this.invitationRoles.length > 0
	}

	async switchOrganization(organizationId) {
		this.error = null
		return this.session.switchOrganization(organizationId)
	}

	async createTeam(value) {
		if (this.creatingTeam) return null
		const name = normalizeTeamName(value)
		if (!name) {
			this.error = 'Введите название агентства.'
			return null
		}
		this.creatingTeam = true
		this.error = null
		try {
			const result = await this.api.createTeam(name)
			await this.session.refreshOrganizationList()
			await this.session.switchOrganization(result.organization.id)
			return result
		} catch (error) {
			this.error = safeOrganizationActionError(error)
			return null
		} finally {
			runInAction(() => { this.creatingTeam = false })
		}
	}

	async createInvitation(emailValue, role) {
		if (this.creatingInvitation) return null
		const email = normalizeInvitationEmail(emailValue)
		if (!email || !this.invitationRoles.includes(role)) {
			this.error = !email ? 'Введите корректный email.' : 'Выберите допустимую роль.'
			return null
		}
		this.creatingInvitation = true
		this.error = null
		try {
			const result = await this.api.createInvitation(email, role)
			runInAction(() => {
				this.pendingInvitations = [result.invitation, ...this.pendingInvitations]
				this.createdInvitationToken = result.token
			})
			return result
		} catch (error) {
			this.error = safeOrganizationActionError(error)
			if (error?.response?.status === 403) await this.session.refreshOrganizations()
			return null
		} finally {
			runInAction(() => { this.creatingInvitation = false })
		}
	}

	async acceptInvitation(tokenValue) {
		if (this.acceptingInvitation) return null
		const token = String(tokenValue || '').trim()
		if (!token) {
			this.error = 'Введите код приглашения.'
			return null
		}
		this.acceptingInvitation = true
		this.error = null
		const identityGeneration = this.identityGeneration
		// A pre-acceptance read must not restore an already accepted invitation.
		this.incomingGeneration += 1
		try {
			const result = await this.api.acceptInvitation(token)
			if (identityGeneration !== this.identityGeneration) return null
			await this.session.refreshOrganizationList()
			if (identityGeneration !== this.identityGeneration) return null
			await this.refreshIncomingInvitations()
			if (identityGeneration !== this.identityGeneration) return null
			return result
		} catch (error) {
			if (identityGeneration === this.identityGeneration) {
				runInAction(() => { this.error = safeOrganizationActionError(error) })
			}
			return null
		} finally {
			if (identityGeneration === this.identityGeneration) {
				runInAction(() => { this.acceptingInvitation = false })
			}
		}
	}

	async refreshIncomingInvitations() {
		const generation = ++this.incomingGeneration
		try {
			const invitations = await this.api.listIncomingInvitations()
			if (generation !== this.incomingGeneration) return []
			runInAction(() => { this.incomingInvitations = invitations })
			return invitations
		} catch {
			if (generation !== this.incomingGeneration) return []
			runInAction(() => { this.incomingInvitations = [] })
			return []
		}
	}

	clearError() {
		this.error = null
	}

	resetTenantUi(reason) {
		// OrganizationSessionStore emits idle on logout and bootstrap for a new identity.
		if (reason === 'idle' || reason === 'bootstrap') this.clearIncomingInvitations()
		this.pendingInvitations = []
		this.createdInvitationToken = null
		this.error = null
	}

	clearIncomingInvitations() {
		this.identityGeneration += 1
		this.incomingGeneration += 1
		this.incomingInvitations = []
		this.acceptingInvitation = false
	}
}

const organizationManagementStore = new OrganizationManagementStore()
registerTenantScopedReset('organization-management-ui', organizationManagementStore.resetTenantUi)

export default organizationManagementStore
